import { useEffect, useRef, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { getTrainers, deleteTrainer } from "../../../services/TrainerService";
import { useAuth } from "../../../context/AuthContext";
import { toast } from '../common/Toast/toast';
import NewCustomDataTable from "../common/NewCustomDataTable.jsx";
import AddTrainer from "./AddTrainer.jsx";
import "./TrainerDashboard.css";
import "../common/lms-pages.css";
import { initialSearchFromUrl } from '../../../utils/urlSearch';
import FilterSheet from '../common/FilterSheet/FilterSheet';
import useDebouncedCallback from '../common/useDebouncedCallback';
import { confirm } from '../common/Confirm/confirm';
import PopupDone, { usePopupDone } from '../common/PopupDone/PopupDone';

const ListTrainer = () => {
  const [data, setData] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [recordsPerPage, setRecordsPerPage] = useState(25);
  const [loading, setLoading] = useState(true);
  // Seeded from ?search= so a hit in the topbar search opens this list
  // already filtered to the row that was clicked.
  const [searchTerm, setSearchTerm] = useState(initialSearchFromUrl);
  const [searchField, setSearchField] = useState("name");
  const [details, setDetails] = useState(null);
  const [formModal, setFormModal] = useState(null);
  const [loadingMore, setLoadingMore] = useState(false);
  // A save in the form popup, or a delete from the view popup, is confirmed
  // in that popup, which closes itself a few seconds later.
  const { done: formDone } = usePopupDone(Boolean(formModal));
  const { done: detailsDone } = usePopupDone(Boolean(details));

  // The boxes hold what was typed; the applied values below are what the
  // list is actually filtered by, so a keystroke does not refetch on its own.
  const [uiSearchTerm, setUiSearchTerm] = useState("");

  // Read before the fetch awaits anything: a reset mid-flight would turn an
  // append into a replace and snap the list back to its first page.
  const appendRef = useRef(false);
  const loadingMoreRef = useRef(false);
  const listEndRef = useRef(null);
  const navigate = useNavigate();
  const hasShownToast = useRef(false);
  const { hasPermission } = useAuth();

  // Permission check
  useEffect(() => {
    if (!hasPermission("list-trainer", "view", "lms")) {
      if (!hasShownToast.current) {
        toast.error("You do not have permission to view trainers.");
        navigate("/");
        hasShownToast.current = true;
      }
    }
  }, [hasPermission, navigate]);

  // Fetch trainers with server-side pagination and search
  const fetchData = useCallback(async () => {
    const isAppend = appendRef.current;
    if (isAppend) setLoadingMore(true);
    else setLoading(true);
    try {
      const response = await getTrainers(
        recordsPerPage,
        currentPage,
        searchTerm,
        searchField,
      );

      const trainers = response?.data || [];
      const pagination = response?.pagination || {};

      // Compute serial number for each row
      const pageOffset = (Number(currentPage) - 1) * recordsPerPage;
      const formattedData = trainers.map((trainer, index) => ({
        ...trainer,
        srno: pageOffset + index + 1,
      }));

      // Guarded by id so a repeated page cannot double up a row.
      if (isAppend) {
        setData((prev) => {
          const seen = new Set(prev.map((r) => r.id));
          return [...prev, ...formattedData.filter((r) => !seen.has(r.id))];
        });
      } else {
      setData(formattedData);
      }
      appendRef.current = false;      setTotalRecords(pagination.total || 0);
    } catch (error) {
      console.error("Error fetching trainers:", error);
      toast.error("Failed to fetch trainers. Please try again.");
    } finally {
      setLoading(false);
      setLoadingMore(false);
      loadingMoreRef.current = false;
    }
  }, [currentPage, recordsPerPage, searchTerm, searchField]);

  // Fetch when dependencies change
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleDelete = async (id) => {
    confirm({
      title: "Delete Confirmation",
      text: "Are you sure you want to delete this trainer?",
      confirmButtonText: "Yes, Delete it!",
      cancelButtonText: "No, Cancel",
      tone: 'danger',
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await deleteTrainer(id);
          // Gone: the view popup says so in place of the record, then closes.
          fetchData(); // re-fetch after deletion
          toast.success("The trainer has been deleted successfully.");
        } catch (error) {
          toast.error("Failed to delete the trainer. Please try again.");
        }
      }
    });
  };

  const hasMore = data.length < totalRecords;

  // The foot of the list asks for the next page when it comes into view.
  useEffect(() => {
    const node = listEndRef.current;
    if (!node || !hasMore || loading) return undefined;
    const io = new IntersectionObserver(
      (entries) => {
        if (!entries.some((e) => e.isIntersecting)) return;
        // `loading` is state and lags a frame, so the ref is what stops one
        // pass asking for two pages.
        if (loadingMoreRef.current) return;
        loadingMoreRef.current = true;
        appendRef.current = true;
        setLoadingMore(true);
        setCurrentPage((p) => p + 1);
      },
      { rootMargin: "150px" },
    );
    io.observe(node);
    return () => io.disconnect();
  }, [hasMore, loading, data.length]);

  const handlePageChange = (page) => {
    if (page !== currentPage) {
      appendRef.current = false;
      setCurrentPage(page);
    }
  };

  const handleRowsPerPageChange = (newLimit) => {
    appendRef.current = false;
    setRecordsPerPage(newLimit);
    setCurrentPage(1);
  };

  /* Typing does not fire a request per keystroke any more -- the box
     and the field beside it still move at once, and the fetch
     underneath waits for the typing to stop. */
  const runSearch = useDebouncedCallback((value) => {
    appendRef.current = false;
    setSearchTerm(value);
    setCurrentPage(1);
  });

  const onSearchChange = (value, field) => {
    setUiSearchTerm(value);
    setSearchField(field);
    runSearch(value);
  };

  // Table headers with custom cells
  const headers = [
    {
      title: "Trainer",
      prop: "trainerName",
      sortable: true,
      cell: (row) => (
        <div className="lp-cell">
          <span className="lp-name">{row.trainerName || "Unnamed"}</span>
          {row.trainerEmail && (
            <span className="lp-sub">{row.trainerEmail}</span>
          )}
        </div>
      ),
    },
    {
      title: "Specialization",
      prop: "specialization",
      sortable: true,
      cell: (row) => (
        <span className="lp-plain">{row.specialization || "—"}</span>
      ),
    },
    {
      title: "Experience",
      prop: "experience",
      sortable: true,
      cell: (row) => (
        <span className="lp-num">
          {row.experience ? `${row.experience} yr` : "—"}
        </span>
      ),
    },
    {
      title: "Added",
      prop: "createdAt",
      sortable: true,
      cell: (row) => (
        <span className="lp-when">
          {row.createdAt
            ? new Date(row.createdAt).toLocaleDateString()
            : "—"}
        </span>
      ),
    },
  ];

  // Searchable fields: map to API field names
  const searchableFields = [
    { value: "trainerName", label: "Trainer Name" },
    { value: "specialization", label: "Specialization" },
  ];

  const canEdit = hasPermission("list-trainer", "update", "lms");
  const canDelete = hasPermission("list-trainer", "delete", "lms");

  return (
    <div className="pg lp">

      <header className="pg-head pg-head--bare">
        <div className="pg-head-actions">
          {hasPermission("list-trainer", "add", "lms") && (
            <button
              type="button"
              className="pg-btn pg-btn--main"
              onClick={() => setFormModal({ recordId: null })}
            >
              Add trainer
            </button>
          )}
        </div>
      </header>

      <FilterSheet label="Filter trainers">
        <div className="pg-filters-search">
          <div className="pg-field pg-field--search">
            <label className="pg-label" htmlFor="lp-trainer-search">
              Search
            </label>
            <input
              id="lp-trainer-search"
              type="search"
              className="pg-input pg-input--search"
              placeholder="Search by trainer name or specialization..."
              value={uiSearchTerm}
              onChange={(e) => onSearchChange(e.target.value, searchField)}
            />
          </div>

          <div className="pg-field pg-field--tight">
            <label className="pg-label" htmlFor="lp-trainer-field">
              Search in
            </label>
            <select
              id="lp-trainer-field"
              className="pg-select"
              value={searchField}
              onChange={(e) => onSearchChange(uiSearchTerm, e.target.value)}
            >
              {searchableFields.map((f) => (
                <option key={f.value} value={f.value}>
                  {f.label}
                </option>
              ))}
            </select>
          </div>

          <div className="pg-field pg-field--tight">
            <label className="pg-label" htmlFor="lp-trainer-rows">
              Rows per load
            </label>
            <select
              id="lp-trainer-rows"
              className="pg-select"
              value={recordsPerPage}
              onChange={(e) => handleRowsPerPageChange(Number(e.target.value))}
            >
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </div>
        </div>
      </FilterSheet>

      <div className="td-list td-list--lms">
        <NewCustomDataTable
          data={data}
          headers={headers}
          totalRecords={totalRecords}
          currentPage={currentPage}
          recordsPerPage={recordsPerPage}
          onPageChange={handlePageChange}
          serverSide={true}
          loading={loading}
          loadingMore={loadingMore}
          showToolbar={false}
          showPagination={false}
          showFooter={false}
          onRowClick={(row) => setDetails(row)}
          validRowClickTagNames="*"
        />
      </div>

      <div className="att-picklist-foot" ref={listEndRef}>
        {loadingMore ? (
          <>
            <span className="pg-spinner att-spinner" aria-hidden="true" />
            <span>Loading more trainers...</span>
          </>
        ) : hasMore ? (
          <span>
            {data.length} of {totalRecords} trainers
          </span>
        ) : (
          totalRecords > 0 && (
            <span>
              All {totalRecords} trainer{totalRecords === 1 ? "" : "s"} loaded
            </span>
          )
        )}
      </div>

      {/* Adding a trainer is one step: who, what they teach, how long. */}
      {formModal && (
        <div
          className="pg-modal pg-modal--over"
          role="dialog"
          aria-modal="true"
          aria-labelledby="lp-trainer-form-title"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) setFormModal(null);
          }}
        >
          <div className="pg-modal-panel pg-modal-panel--wide pg-modal-panel--form">
            <header className="pg-modal-head">
              <div className="pg-modal-heading">
                <span className="pg-modal-eyebrow">Trainer</span>
                <h3 className="pg-modal-title" id="lp-trainer-form-title">
                  {formModal.recordId ? "Edit trainer" : "Add a trainer"}
                </h3>
                <p className="pg-modal-sub">
                  Pick the user, then say what they teach.
                </p>
              </div>
              <button
                type="button"
                className="pg-modal-close"
                onClick={() => setFormModal(null)}
                aria-label="Close"
              >
                &times;
              </button>
            </header>

            <div className="pg-modal-body">
              {formDone ? (
                <PopupDone message={formDone} onClose={() => setFormModal(null)} />
              ) : (
              <AddTrainer
                embedded
                recordId={formModal.recordId}
                onCancel={() => setFormModal(null)}
                onDone={() => {
                  // Saved: the popup shows the message in place of the form and
                  // closes itself a few seconds later.
                  // The record changed, so the view popup still open behind this form
                  // would be showing the old one. It goes; the form stays.
                  setDetails(null);
                  appendRef.current = false;
                  setCurrentPage(1);
                  fetchData();
                }}
              />
              )}
            </div>
          </div>
        </div>
      )}

      {details && (
        <div
          className="pg-modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="lp-trainer-title"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) setDetails(null);
          }}
        >
          <div className="pg-modal-panel pg-modal-panel--wide">
            <header className="pg-modal-head">
              <div className="pg-modal-heading">
                <span className="pg-modal-eyebrow">Trainer</span>
                <h3 className="pg-modal-title" id="lp-trainer-title">
                  {details.trainerName || "Unnamed"}
                </h3>
                {details.trainerEmail && (
                  <p className="pg-modal-sub">{details.trainerEmail}</p>
                )}
              </div>
              <button
                type="button"
                className="pg-modal-close"
                onClick={() => setDetails(null)}
                aria-label="Close"
              >
                &times;
              </button>
            </header>

            <div className="pg-modal-body">
              {detailsDone ? (
                <PopupDone message={detailsDone} onClose={() => setDetails(null)} />
              ) : (
              <dl className="quiz-facts">
                <div>
                  <dt>Specialization</dt>
                  <dd>{details.specialization || "—"}</dd>
                </div>
                <div>
                  <dt>Experience</dt>
                  <dd>
                    {details.experience ? `${details.experience} years` : "—"}
                  </dd>
                </div>
                <div>
                  <dt>Phone</dt>
                  <dd>{details.trainerMobile || details.mobile || "—"}</dd>
                </div>
                <div>
                  <dt>Added</dt>
                  <dd>
                    {details.createdAt
                      ? new Date(details.createdAt).toLocaleString()
                      : "—"}
                  </dd>
                </div>
              </dl>
              )}
            </div>

            {!detailsDone && (
            <footer className="pg-modal-foot">
              <div className="pgm-actions">
                {canEdit && (
                  <button
                    type="button"
                    className="pgm-btn"
                    onClick={() => {
                      const id = details.id;
                      setFormModal({ recordId: id });
                    }}
                  >
                    Edit trainer
                  </button>
                )}
                {canDelete && (
                  <button
                    type="button"
                    className="pgm-btn pgm-btn--danger"
                    onClick={() => {
                      const id = details.id;
                      handleDelete(id);
                    }}
                  >
                    Delete trainer
                  </button>
                )}
              </div>
              <button
                type="button"
                className="pg-btn pg-btn--main"
                onClick={() => setDetails(null)}
              >
                Close
              </button>
            </footer>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ListTrainer;
